import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { captureException } from '@sentry/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdEdit } from 'react-icons/md'
import {
  ConnectionLineType,
  MarkerType,
  Panel,
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  MiniMap,
  addEdge,
  updateEdge,
} from 'reactflow'

import type { ReactElement } from 'react'
import type { Connection, Edge } from 'reactflow'
import type { ITodolistTemplate, ITodolistTemplateTask } from '~/types/todolist-template'
import type { IUserPublic } from '~/types/user'

import SetTodolistTemplateModal from '../SetTodolistTemplateModal'
import SetTodolistTemplateTaskModal from '../SetTodolistTemplateTaskModal'

import BackNavigation from '~/components/BackNavigation'
import Button from '~/components/Buttons/Button'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import Resizer from '~/components/ReactFlow/Resizer'
import UserCard from '~/components/UserCard'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useRequiredParams } from '~/hooks/useRequiredParams'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useTodolistTemplateQuery, useUpdateTodolistTemplateMutation } from '~/queries/todolist-templates'
import { useUserDetailsQuery } from '~/queries/users'
import { edgeTypes, getLayoutedElements, nodeTypes } from '~/utils/react-flow'
import { displayToast } from '~/utils/toast'

import './styles.scss'
import 'reactflow/dist/style.css'

const TodolistTemplateDetails = (): ReactElement => {
  /** Local state */

  const { templateId } = useRequiredParams<{ templateId: string }>()

  const isAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.TODOLIST.WRITE])

  const edgeUpdateSuccessful = useRef(true)

  const [isUpdateTodolistTemplateOpen, setUpdateTodolistTemplateOpen] = useState(false)

  const [isCreateOrEditTaskModalOpen, setCreateOrEditTaskModalOpen] = useState(false)

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const [nodes, setNodes, onNodesChange] = useNodesState<ITodolistTemplateTask>([])

  const [edges, setEdges, onEdgesChange] = useEdgesState<ITodolistTemplateTask>([])

  const [selectedTask, setSelectedTask] = useState<ITodolistTemplateTask>()

  const [localTemplate, setLocalTemplate] = useState<ITodolistTemplate>()

  /** Queries & Mutations */

  const { mutateAsync: updateTodolistTemplate } = useUpdateTodolistTemplateMutation()

  const { data: todolistTemplate, isLoading } = useTodolistTemplateQuery(templateId)

  const formatCreateAtUser = ({ name, picture, profile }: IUserPublic) => ({
    date: todolistTemplate?.template.created_at || 0,
    job: profile?.job_title || '',
    name,
    picture,
  })

  const formatUpdateAtUser = ({ name, picture, profile }: IUserPublic) => ({
    date: todolistTemplate?.template.updated_at || 0,
    job: profile?.job_title || '',
    name,
    picture,
  })

  const { data: createdByUser } = useUserDetailsQuery(
    todolistTemplate?.template.created_by,
    !!todolistTemplate,
    formatCreateAtUser
  )

  const { data: updatedByUser } = useUserDetailsQuery(
    todolistTemplate?.template.updated_by,
    !!todolistTemplate,
    formatUpdateAtUser
  )

  /** Effects */

  useDocumentTitle('Todolist Template Details')

  useEffect(() => {
    if (todolistTemplate) {
      setLocalTemplate(todolistTemplate)
    }
  }, [todolistTemplate])

  /**
   * Initialize nodes and edges when data is fetched
   */
  useEffect(() => {
    if (localTemplate) {
      const initialNodes = []
      const initialEdges = []

      for (const task of localTemplate.tasks) {
        initialNodes.push({
          data: {
            ...task,
            isEditable: isAuthorized,
            onDelete: handleDeleteTask,
            onEdit: handleEditTask,
          },
          id: task.template_task_id,
          position: { x: 0, y: 0 },
          type: 'todolist-template-task-card',
        })

        if (task.previous_steps && task.previous_steps.length) {
          for (const previousStepId of task.previous_steps) {
            initialEdges.push({
              data: {
                todolistTemplate: localTemplate,
              },
              id: `${previousStepId}-${task.template_task_id}`,
              markerEnd: {
                type: MarkerType.ArrowClosed,
              },
              source: previousStepId,
              target: task.template_task_id,
              type: isAuthorized ? 'todolist-template-button-edge' : 'smoothstep',
            })
          }
        }
      }

      const { edges: layoutedEdges, nodes: layoutedNodes } = getLayoutedElements(initialNodes, initialEdges, {
        direction: 'TB',
        nodeHeight: 350,
        nodeWidth: 325,
      })

      setNodes(layoutedNodes)
      setEdges(layoutedEdges)
    }
  }, [isAuthorized, localTemplate, setNodes, setEdges])

  /** Handlers */

  const handleUpdateTodolistTemplateState = (isOpen: boolean) => () => setUpdateTodolistTemplateOpen(isOpen)

  const handleCreateOrUpdateTaskModalState = (isOpen: boolean) => () => {
    setCreateOrEditTaskModalOpen(isOpen)
    setSelectedTask(undefined)
  }

  const handleDeleteModalState = (isOpen: boolean) => () => setDeleteModalOpen(isOpen)

  const handleEditTask = (task: ITodolistTemplateTask) => {
    setSelectedTask(task)
    setCreateOrEditTaskModalOpen(true)
  }

  const handleDeleteTask = (task: ITodolistTemplateTask) => {
    setSelectedTask(task)
    setDeleteModalOpen(true)
  }

  const handleDeleteTaskConfirm = async () => {
    if (isAuthorized && localTemplate && selectedTask) {
      try {
        const removeFromTaskList = (t: ITodolistTemplateTask) => t.template_task_id !== selectedTask.template_task_id

        const removeFromPreviousSteps = (t: ITodolistTemplateTask) =>
          t.previous_steps.filter((s) => s !== selectedTask.template_task_id)

        const newTasks = localTemplate.tasks
          .filter(removeFromTaskList)
          .map((t) => ({ ...t, previous_steps: removeFromPreviousSteps(t) }))

        await updateTodolistTemplate({
          data: {
            tasks: newTasks,
            template: localTemplate.template,
          },
          templateId: localTemplate.template.template_id,
        })

        setLocalTemplate({ tasks: newTasks, template: localTemplate.template })
        setDeleteModalOpen(false)

        const updatedNodes = nodes.filter((n) => n.data.template_task_id !== selectedTask.template_task_id)

        setNodes(updatedNodes)
        setSelectedTask(undefined)
        displayToast('Task successfully deleted', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Task could not be deleted!', 'error')
      }
    }
  }

  /**
   * Trigger when a new edge is created
   */
  const handleConnect = useCallback(
    async (edgeParams: Edge | Connection) => {
      const { source, target } = edgeParams
      const targetNode = nodes.find(({ id }) => id === target)

      if (isAuthorized && localTemplate && target && targetNode && source) {
        try {
          const newTasks = localTemplate.tasks.map((t) =>
            t.template_task_id === target ? { ...t, previous_steps: [...new Set([...t.previous_steps, source])] } : t
          )

          await updateTodolistTemplate({
            data: {
              tasks: newTasks,
              template: localTemplate.template,
            },
            templateId: localTemplate.template.template_id,
          })

          setEdges((edg) =>
            addEdge(
              {
                ...edgeParams,
                type: 'app-setup-button-edge',
              },
              edg
            )
          )
          setLocalTemplate({ tasks: newTasks, template: localTemplate.template })
        } catch (e) {
          captureException(e)
          displayToast('Tasks could not be linked!', 'error')
        }
      }
    },
    [isAuthorized, localTemplate, nodes, setEdges, updateTodolistTemplate]
  )

  const handleEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false
  }, [])

  const handleEdgeUpdate = useCallback(
    async (oldEdge: Edge, newConnection: Connection) => {
      // NOTE: set new connection
      const { source, target } = newConnection
      const targetNode = nodes.find(({ id }) => id === target)

      if (isAuthorized && localTemplate && target && targetNode && source && oldEdge.target !== target) {
        try {
          const newTasks = localTemplate.tasks.map((t) => {
            if (t.template_task_id === oldEdge.target) {
              return { ...t, previous_steps: t.previous_steps.filter((s) => s !== source) }
            } else if (t.template_task_id === target) {
              return { ...t, previous_steps: [...new Set([...t.previous_steps, source])] }
            }
            return t
          })

          await updateTodolistTemplate({
            data: {
              tasks: newTasks,
              template: localTemplate.template,
            },
            templateId: localTemplate.template.template_id,
          })

          setEdges((eds) => updateEdge(oldEdge, newConnection, eds))
          setLocalTemplate({ tasks: newTasks, template: localTemplate.template })
        } catch (e) {
          captureException(e)
          displayToast('Tasks could not be linked!', 'error')
        }
      }

      edgeUpdateSuccessful.current = true
    },
    [isAuthorized, localTemplate, nodes, setEdges, updateTodolistTemplate]
  )

  const handleEdgeUpdateEnd = useCallback(
    async (_: MouseEvent | TouchEvent, oldEdge: Edge) => {
      // NOTE: remove old connection
      if (!edgeUpdateSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== oldEdge.id))
      }

      edgeUpdateSuccessful.current = true
    },
    [setEdges]
  )

  const authorizedActions = useMemo(() => {
    return isAuthorized
      ? {
          onConnect: handleConnect,
          onEdgeUpdate: handleEdgeUpdate,
          onEdgeUpdateEnd: handleEdgeUpdateEnd,
          onEdgeUpdateStart: handleEdgeUpdateStart,
          onEdgesChange,
          onNodesChange,
        }
      : {}
  }, [
    handleConnect,
    handleEdgeUpdate,
    handleEdgeUpdateEnd,
    handleEdgeUpdateStart,
    onEdgesChange,
    onNodesChange,
    isAuthorized,
  ])

  return (
    <Layout className="todolist-template-details-view" withPadding>
      <ViewTitle>
        <BackNavigation title="Todolist templates" to="/todolist-templates" />
      </ViewTitle>
      <div className="todolist-template-details-view__general-information-container">
        <div className="flex-row-container space-between">
          <div className="flex-row-container">
            <div className="todolist-template-details-view__general-information-container__title-container">
              <div className="title">{localTemplate?.template.title}</div>
              <div className="description">{localTemplate?.template.description}</div>
            </div>
            <Button className="edit-button" onClick={handleUpdateTodolistTemplateState(true)} outlined size="small">
              <MdEdit color="#ff5301" size={14} />
              Edit
            </Button>
          </div>
          <div className="flex-row-container">
            <div className="column">
              {createdByUser ? (
                <UserCard
                  label="Created by"
                  name={createdByUser.name}
                  picture={createdByUser.picture}
                  size="small"
                  subInfo={formatDate(createdByUser.date, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                />
              ) : null}
            </div>
            <div className="column">
              {updatedByUser ? (
                <UserCard
                  label="Last update by"
                  name={updatedByUser.name}
                  picture={updatedByUser.picture}
                  size="small"
                  subInfo={formatDate(updatedByUser.date, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="loader-container">
          <MwMLoader category="item" />
        </div>
      ) : (
        <>
          <ReactFlow
            connectionLineType={ConnectionLineType.SmoothStep}
            edgeTypes={edgeTypes}
            edges={edges}
            fitView
            maxZoom={1}
            nodeTypes={nodeTypes}
            nodes={nodes}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
            {...authorizedActions}
          >
            <Background />
            <MiniMap />
            <Resizer />
            {isAuthorized ? (
              <Panel className="top-left-panel" position="top-left">
                <Button className="add-task-button" onClick={handleCreateOrUpdateTaskModalState(true)}>
                  + task
                </Button>
              </Panel>
            ) : null}
          </ReactFlow>
          {localTemplate ? (
            <SetTodolistTemplateTaskModal
              isOpen={isCreateOrEditTaskModalOpen}
              onClose={handleCreateOrUpdateTaskModalState(false)}
              onSuccess={handleCreateOrUpdateTaskModalState(false)}
              task={selectedTask}
              todolistTemplate={localTemplate}
            />
          ) : null}
          <SetTodolistTemplateModal
            isOpen={isUpdateTodolistTemplateOpen}
            onClose={handleUpdateTodolistTemplateState(false)}
            onSuccess={handleUpdateTodolistTemplateState(false)}
            todolistTemplate={localTemplate}
          />
          <ModalDialog
            isOpen={isDeleteModalOpen}
            mainText="Do you really want to delete this task?"
            onClose={handleDeleteModalState(false)}
            onConfirm={handleDeleteTaskConfirm}
          />
        </>
      )}
    </Layout>
  )
}

export default TodolistTemplateDetails
